import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { FileText, Calendar } from 'lucide-react';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';
import ReportExportButton from '@/components/reports/ReportExportButton';

const SUMMARY_LABEL_KEYS = [
  'reports.openingBalance',
  'reports.cashIn',
  'reports.cashOut',
  'reports.returns',
  'reports.closingBalance',
] as const;

export default function DailyCashLedgerSection() {
  const { t } = useTranslation();
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dailyCashLedger', date],
    queryFn: () => reportsApi.getDailyCashLedger(date),
  });

  const rows = data?.rows || [];
  const summary = data?.summary || [];

  const handleExport = () => {
    if (!rows.length) return;
    const headers = [
      { key: 'time', label: t('reports.time') },
      { key: 'voucher_type', label: t('reports.voucher') },
      { key: 'voucher_no', label: t('reports.invoice') },
      { key: 'party', label: t('reports.party') },
      { key: 'debit_amount', label: t('reports.in') },
      { key: 'credit_amount', label: t('reports.out') },
      { key: 'balance', label: t('reports.balance') },
      { key: 'remarks', label: t('reports.remarks') },
    ];
    downloadCsv(`Daily_Cash_Ledger_${date}.csv`, headers, rows);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border-gray-200 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <ReportExportButton onClick={handleExport} disabled={!rows.length || isLoading} />
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {t('reports.loadFailed')}
        </div>
      )}

      {summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {summary.map((card: { label: string; value: number; indicator?: string }, idx: number) => (
            <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-sm font-medium text-gray-500">
                {SUMMARY_LABEL_KEYS[idx] ? t(SUMMARY_LABEL_KEYS[idx]) : card.label}
              </p>
              <p
                className={`text-xl font-bold mt-1 ${
                  card.indicator === 'Red'
                    ? 'text-red-600'
                    : card.indicator === 'Green'
                      ? 'text-emerald-600'
                      : 'text-gray-900'
                }`}
              >
                {formatMoney(card.value)}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.time')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.voucher')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.party')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.in')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.out')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.balance')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.remarks')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="inline-flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500" />
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500 animation-delay-200" />
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500 animation-delay-400" />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">{t('reports.loadingLedger')}</p>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-900">{t('reports.noTransactions')}</p>
                    <p className="text-sm">{t('reports.noTransactionsHelp')}</p>
                  </td>
                </tr>
              ) : (
                rows.map((row: {
                  time: string;
                  voucher_type: string;
                  voucher_no: string;
                  party?: string;
                  debit_amount: number;
                  credit_amount: number;
                  balance: number;
                  remarks?: string;
                }, i: number) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.time}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium text-primary-600">{row.voucher_no}</div>
                      <div className="text-xs text-gray-500">{row.voucher_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.party || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-emerald-600 font-medium">
                      {row.debit_amount > 0 ? formatMoney(row.debit_amount) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                      {row.credit_amount > 0 ? formatMoney(row.credit_amount) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                      {formatMoney(row.balance)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{row.remarks || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
